import moment, { Moment, isMoment } from 'moment';
import isNil from 'lodash/isNil';

export { Moment };

export function getCurrentMoment() {
  return moment();
}

export function convertToMoment(date: string | Date | number | Moment) {
  if (isMoment(date)) return date;
  return moment(date);
}

export function convertMomentToUnix(date: Moment) {
  return moment(date).unix();
}

export function convertUnixToMoment(date: number) {
  return moment.unix(date);
}

export function dateMMMDDYYYY(date: string | Date) {
  return moment(date).format('MMM DD, YYYY');
}

export function dateMMMDDYYYYatHHMM(date: string | Date) {
  const dateValue = moment(date).format('MMM DD, YYYY');
  const timeValue = moment(date).format('hh:mma');
  return `${dateValue} at ${timeValue}`;
}

export function unixDateMMMDDYYYY(date: number) {
  return moment.unix(date).format('MMM D, YYYY');
}

export function datedddMMYY(date: string | Date) {
  return moment(date).format('ddd, MMMM D, YYYY');
}

export function dateMMDDYY(date: string | Date) {
  return moment(date).format('M/D/YYYY');
}

export function isFuture(time: number) {
  return moment().unix() < time;
}

export function isInThePast(time: number) {
  return moment().unix() > time;
}

export function timestampToDate(timestamp: number) {
  const date = new Date();
  date.setTime(timestamp * 1000);
  return date;
}

export function militaryTimeTo12hours(hours: number) {
  return hours ? moment(hours, 'HH:mm').format('LT') : moment().format('LT');
}

export const getIsDateBefore = (dateToCheck: Moment, targetDate: Moment = getCurrentMoment()) => {
  return convertMomentToUnix(dateToCheck) > convertMomentToUnix(targetDate);
};

export const getIsDateAfter = (dateToCheck: string | Date, targetDate: number | Date = Date.now()) => {
  if (isNil(dateToCheck) || isNil(targetDate)) return false;
  return moment(dateToCheck).diff(moment(targetDate)) < 0;
};

export function dueDateTimeFormat(date: Moment, time: string) {
  if (!date || !isMoment(date)) return null;
  return moment(date.format('YYYY-MM-DD ') + time, 'YYYY-MM-DD HH:mm').unix();
}

export function getTimeAgoLabel(datetime: string | Date, includeTime = true) {
  const now = moment();

  const createdDaysAgo = now.diff(moment(datetime), 'days');
  if (createdDaysAgo > 7 && includeTime) return dateMMMDDYYYYatHHMM(datetime);

  return moment(datetime).fromNow();
}

export function convertTimeTo24Hour(time: string) {
  return moment(time, 'hh:mm a').format('HH:mm');
}

export function convert24HourTimeToSeconds(time: string) {
  return moment.duration(time).asSeconds();
}

export function convertUnixAndSecondsToDateTime(unix: number, seconds: number) {
  return moment.unix(unix).startOf('day').add(seconds, 'seconds').unix();
}

export function formatDate(date: Moment | string | number | Date, format: string) {
  if (isMoment(date)) return date.format(format);
  if (typeof date === 'number') return convertUnixToMoment(date).format(format);
  else return convertToMoment(date).format(format);
}

export function isDateInCurrentYear(date: Moment | string | number) {
  const currentYear = new Date().getFullYear();
  return currentYear.toString() === formatDate(date, 'YYYY');
}

export function getISOString(date: Moment | string | number | Date) {
  if (isMoment(date)) return date.toISOString();
  if (typeof date === 'number') return convertUnixToMoment(date).toISOString();
  else return convertToMoment(date).toISOString();
}

export const areDatesEqual = (dateA: Moment | string | number | Date, dateB: Moment | string | number | Date) => {
  const dateAFormated = isMoment(dateA) ? dateA : convertToMoment(dateA);
  const dateBFormated = isMoment(dateB) ? dateB : convertToMoment(dateB);
  return dateAFormated.isSame(dateBFormated);
};

/**
 * Checks if a given date (`dateToCheck`) is strictly after the target date (`targetDate`).
 *
 * - Returns `true` if `dateToCheck` is after `targetDate`.
 * - Returns `false` if `dateToCheck` is before or the same as `targetDate`.
 *
 * @param {string | Date | number | Moment} dateToCheck - The date that you want to check. Can be a string (ISO format) or a Date object or Moment or number.
 * @param {string | Date | number | Moment} [targetDate=Date.now()] - The target date to compare against. Can be a timestamp (number) or a Date object or Moment or String. Defaults to the current date and time if not provided.
 *
 * @returns {boolean} - Returns `true` if `dateToCheck` is strictly after `targetDate`, otherwise `false`.
 *
 * @example
 * const date1 = '2024-01-01';
 * const date2 = '2023-12-31';
 * const sameDate = '2024-01-01';
 *
 * getIsDateStrictlyAfter(date1, date2); // true (Jan 1st, 2024 is after Dec 31st, 2023)
 * getIsDateStrictlyAfter(date1, sameDate); // false (Same dates return false)
 * getIsDateStrictlyAfter(date2, date1); // false (Dec 31st, 2023 is before Jan 1st, 2024)
 */
export const getIsDateStrictlyAfter = (
  dateToCheck: string | Date | number | Moment,
  targetDate: string | Date | number | Moment = Date.now()
) => {
  if (isNil(dateToCheck) || isNil(targetDate)) return false;
  return moment(dateToCheck).diff(moment(targetDate)) > 0;
};

/**
 * Checks if the dateToCheck is either the same as or strictly after the targetDate.
 *
 * @param {string | Date | number | Moment} dateToCheck - The date to be checked against the targetDate.
 * @param {string | Date | number | Moment} [targetDate=Date.now()] - The target date to compare against. Defaults to the current date/time.
 * @returns {boolean} - Returns true if dateToCheck is the same as or strictly after targetDate; otherwise, false.
 */
export const getIsDateSameOrAfter = (
  dateToCheck: string | Date | number | Moment,
  targetDate: string | Date | number | Moment = Date.now()
): boolean => {
  if (isNil(dateToCheck) || isNil(targetDate)) return false;

  // Check if the date is the same or strictly after
  return moment(dateToCheck).isSameOrAfter(moment(targetDate));
};
